import { observable, action, toJS } from "mobx"
// import { ignore } from 'mobx-sync'
import api from "../services/ApiDefault"
import { showResponseError } from "../utilities/utils"
import routerStore from "./RouterStore"
import moment from "moment"
import { saveAs } from "file-saver"
import ExcelJS from "exceljs"
import authStore from "./AuthStore"
import { isReadOnly } from "../utilities/permissions"
export class ReservationStore {
  @observable isLoading = false
  @observable isUploading = false
  @observable isUpdating = false

  @observable reservations = []
  @observable
  params = {
    perPage: 10,
    page: 1,
    status: "",
    reservation_type: "",
    club_id: "",
  }
  @observable userBookings = []

  @observable summary = []
  @observable summaryPreset = "today"
  @observable isSummaryLoading = false

  getSummaryDateRange(preset) {
    const today = moment().startOf("day")
    switch (preset) {
      case "tomorrow": {
        const tomorrow = today.clone().add(1, "day")
        return { from: tomorrow.format("YYYY-MM-DD"), to: tomorrow.format("YYYY-MM-DD") }
      }
      case "weekend": {
        const daysUntilFriday = (5 - today.day() + 7) % 7
        const friday = today.clone().add(daysUntilFriday, "days")
        const sunday = friday.clone().add(2, "days")
        return { from: friday.format("YYYY-MM-DD"), to: sunday.format("YYYY-MM-DD") }
      }
      case "7days":
        return {
          from: today.format("YYYY-MM-DD"),
          to: today.clone().add(7, "days").format("YYYY-MM-DD"),
        }
      case "14days":
        return {
          from: today.format("YYYY-MM-DD"),
          to: today.clone().add(14, "days").format("YYYY-MM-DD"),
        }
      case "21days":
        return {
          from: today.format("YYYY-MM-DD"),
          to: today.clone().add(21, "days").format("YYYY-MM-DD"),
        }
      case "today":
      default:
        return { from: today.format("YYYY-MM-DD"), to: today.format("YYYY-MM-DD") }
    }
  }

  @action
  async getBookingSummary(preset, filters) {
    if (preset) this.summaryPreset = preset
    this.isSummaryLoading = true
    const { from, to } = this.getSummaryDateRange(this.summaryPreset)
    const source = filters || this.params
    const params = {
      from_date: from,
      to_date: to,
    }
    if (source.club_id) params.club_id = source.club_id
    if (source.reservation_type) params.reservation_type = source.reservation_type
    if (source.status) params.status = source.status
    const response = await api.getReservationSummary(params)
    if (response.ok) {
      this.summary = response.data.data
    } else {
      showResponseError(response)
    }
    this.isSummaryLoading = false
  }

  @action
  async getBookings(params = {}) {
    this.isLoading = true
    const query = {
      where: {},
      // where: { status: { $ne: 'cancelled' } },
      with: ["user", "club", "cancelledBy"],
      sort: { date: "desc", created_at: "desc" },
    }
    params = {
      ...toJS(this.params),
      ...params,
    }
    if (params.status) {
      query.where.status = params.status
    }
    if (params.club_id) {
      query.where.club_id = params.club_id
    }
    if (params.reservation_type) {
      query.where.reservation_type = params.reservation_type
    }
    const response = await api.getReservations({ query, ...params })
    if (response.ok) {
      this.reservations = response.data.data
      this.params = {
        ...params,
        page: response.data.page,
        lastPage: response.data.lastPage,
        perPage: response.data.perPage,
        total: response.data.total,
      }
    } else {
      showResponseError(response)
    }
    this.isLoading = false
  }

  @action
  async getUserBookings(userId) {
    this.userBookings = []
    this.isLoading = true
    const query = {
      where: { user_id: userId },
      with: ["user", "club", "cancelledBy"],
      sort: { date: "desc", created_at: "desc" },
    }
    const response = await api.getReservations({ query })
    if (response.ok) {
      this.userReservations = response.data.data
    } else {
      showResponseError(response)
    }
    this.isLoading = false
  }

  @action
  async updateReservation(id, data) {
    const response = await api.updateReservation(id, data)
    if (response.ok) {
      routerStore.replace(`/bookings/${id}`)
      return true
    } else {
      showResponseError(response)
      return false
    }
  }

  @action
  async confirmReservation(id, status) {
    const response = await api.confirmReservation(id, status)
    if (response.ok) {
      return response?.data?.data
    } else {
      showResponseError(response)
      return false
    }
  }

  @action
  async downloadBookings() {
    if (isReadOnly(authStore.user)) return
    this.isLoading = true
    const query = {
      where: {},
      // where: { status: { $ne: 'cancelled' } },
      with: ["user", "club", "cancelledBy"],
      sort: { date: "desc", created_at: "desc" },
    }
    const response = await api.getReservations({ query, perPage: 100000 })
    if (response.ok) {
      const data = response.data.data.map((reservation) => {
        const reservationDate = moment(reservation.date).endOf("day").add(3, "hours")
        let status = reservation.status
        if (status === "opening" && reservationDate.diff(moment()) < 0) {
          status = "No Show"
        }

        return {
          _id: reservation._id,
          name: reservation.user?.name,
          type: reservation.reservation_type,
          club: reservation.club?.name,
          men: reservation.gentlemen,
          girl: reservation.ladies,
          spend: reservation.price,
          date: moment(reservation.created_at).format("DD/MM/YYYY HH:mm"),
          for: reservation.date,
          confirmation: reservation.confirmation_status,
          status: status,
          checked_in_time: reservation.checked_in_time,
          cancelled_time: reservation.cancelled_time,
          cancelled_by: reservation.cancelledBy,
        }
      })

      const header = [
        "_id",
        "name",
        "type",
        "club",
        "men",
        "girl",
        "spend",
        "date",
        "for",
        "confirmation",
        "status",
        "checked_in_time",
        "cancelled_time",
        "cancelled_by",
      ]

      const workbook = new ExcelJS.Workbook()
      const sheet = workbook.addWorksheet("Bookings")

      // Add header row with styling
      sheet.addRow(header)
      const headerRow = sheet.getRow(1)
      headerRow.font = { bold: true }
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "BFBFBF" },
      }

      // Add data rows
      data.forEach((item) => {
        const rowData = header.map((key) => item[key])
        sheet.addRow(rowData)
      })

      // Add border to all cells
      sheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          }
        })
      })

      // Generate buffer and save file
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
        saveAs(blob, `Bookings_${new Date().toISOString()}.xlsx`)
      })
    } else {
      showResponseError(response)
    }
    this.isLoading = false
  }
}

const reservationStore = new ReservationStore()
export default reservationStore
